import { useMutation, useQueryClient } from "@tanstack/react-query";
import supabase from "@/supabase";
import * as Sentry from "@sentry/react";

export type AssessmentType =
  | "orientation-quiz"
  | "sjt"
  | "driver-onroad-test";

export interface ResultBreakdown {
  answers: Record<string, string>; // Question ID to Answer ID mapping
}

export interface AssessmentData {
  applicationId: string;
  type: AssessmentType;
  score: number;
  result?: string; // pass/fail or other result classification
  resultBreakdown?: ResultBreakdown; // JSON data for detailed results
}

/**
 * Hook for submitting assessment results to the database
 */
export const useSubmitAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      type,
      score,
      result,
      resultBreakdown,
    }: AssessmentData) => {
      if (!applicationId || !type) {
        throw new Error("Application ID and assessment type are required");
      }

      try {
        const now = new Date();
        // Format as YYYY-MM-DD HH:MM:SS to avoid timezone issues with timestamp without timezone
        const formattedDate = now.toISOString().slice(0, 19).replace("T", " ");

        // Check if an assessment of this type already exists for this application
        const { data: existingAssessment } = await supabase
          .from("assessments")
          .select("id")
          .eq("application_id", applicationId)
          .eq("type", type)
          .maybeSingle();

        let response;

        if (existingAssessment) {
          // Update existing assessment
          response = await supabase
            .from("assessments")
            .update({
              completed_at: formattedDate,
              score: score,
              result: result,
              result_breakdown: resultBreakdown || {},
            })
            .eq("id", existingAssessment.id)
            .select()
            .single();
        } else {
          // Create new assessment
          response = await supabase
            .from("assessments")
            .insert([
              {
                application_id: applicationId,
                type: type,
                started_at: formattedDate,
                completed_at: formattedDate,
                score: score,
                result: result,
                result_breakdown: resultBreakdown || {},
              },
            ])
            .select()
            .single();
        }

        const { data, error } = response;

        if (error) {
          console.error("Error submitting assessment:", error);
          Sentry.captureException(error, {
            extra: {
              applicationId,
              type,
              score,
              result,
              resultBreakdown,
            },
          });
          throw new Error("Failed to submit assessment");
        }

        return {
          success: true,
          assessment: data,
        };
      } catch (error) {
        console.error("Error in submitAssessment:", error);
        Sentry.captureException(error, {
          extra: {
            applicationId,
            type,
            score,
            result,
            resultBreakdown,
          },
        });
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["application"] });
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
};

/**
 * Hook for starting an assessment (recording the start time)
 */
export const useStartAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      type,
    }: Pick<AssessmentData, "applicationId" | "type">) => {
      if (!applicationId || !type) {
        throw new Error("Application ID and assessment type are required");
      }

      try {
        const now = new Date();
        // Format as YYYY-MM-DD HH:MM:SS to avoid timezone issues
        const formattedDate = now.toISOString().slice(0, 19).replace("T", " ");

        // Check if an assessment of this type already exists for this application
        const { data: existingAssessment } = await supabase
          .from("assessments")
          .select("id")
          .eq("application_id", applicationId)
          .eq("type", type)
          .maybeSingle();

        if (existingAssessment) {
          // Assessment already exists, just return it
          return {
            success: true,
            assessmentId: existingAssessment.id,
          };
        }

        // Create new assessment with just the start time
        const { data, error } = await supabase
          .from("assessments")
          .insert([
            {
              application_id: applicationId,
              type: type,
              started_at: formattedDate,
            },
          ])
          .select()
          .single();

        if (error) {
          console.error("Error starting assessment:", error);
          Sentry.captureException(error, {
            extra: {
              applicationId,
              type,
            },
          });
          throw new Error("Failed to start assessment");
        }

        return {
          success: true,
          assessmentId: data.id,
        };
      } catch (error) {
        console.error("Error in startAssessment:", error);
        Sentry.captureException(error, {
          extra: {
            applicationId,
            type,
          },
        });
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
};
